import { useId } from "react";

/**
 * Turns a series into a smooth path across a w x h box.
 *
 * The curve uses tangents taken from each point's neighbours, scaled to a third
 * of the gap. A plain Catmull-Rom through evenly spaced x values would overshoot
 * on a spike and dip the line below its own minimum, which on a revenue chart
 * reads as a month that never happened.
 *
 * `range` is passed in rather than measured off `values`, because two series
 * drawn on one chart have to share a scale. Letting each normalise to its own
 * min and max is the classic way to produce a comparison line that swings
 * wildly against a flat one while both describe the same money.
 */
function smoothPath(values: number[], w: number, h: number, pad: number, range: [number, number]): string {
  if (values.length < 2) return "";
  const [min, max] = range;
  const span = max - min || 1;
  const pts = values.map((v, i) => ({
    x: (i / (values.length - 1)) * w,
    y: h - pad - ((v - min) / span) * (h - pad * 2),
  }));

  let d = `M${pts[0]!.x},${pts[0]!.y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p3 = pts[i + 2] ?? p2;
    const c1 = { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 };
    const c2 = { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 };
    d += ` C${c1.x},${c1.y} ${c2.x},${c2.y} ${p2.x},${p2.y}`;
  }
  return d;
}

/**
 * The line that runs along the bottom of a KPI tile: a filled gradient under a
 * stroked curve, bleeding into the card's own corners.
 */
export function Sparkline({ values, color }: { values: number[]; color: string }) {
  const id = useId().replace(/:/g, "");
  const W = 200;
  const H = 42;
  const line = smoothPath(values, W, H, 6, [Math.min(...values), Math.max(...values)]);
  if (!line) return null;
  return (
    <svg className="crm-kpi-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id={`sp-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity=".22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${line} L${W},${H} L0,${H} Z`} fill={`url(#sp-${id})`} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/** The same curve at chart size, with a comparison line behind it. */
export function AreaChart({
  values,
  compare,
  color,
  height = 210,
}: {
  values: number[];
  compare?: number[];
  color: string;
  height?: number;
}) {
  const id = useId().replace(/:/g, "");
  const W = 600;
  const H = 210;
  // Both series share one scale, or the comparison line would say nothing.
  const all = compare ? values.concat(compare) : values;
  const range: [number, number] = [Math.min(...all, 0), Math.max(...all, 1)];
  const line = smoothPath(values, W, H, 22, range);
  const cmp = compare ? smoothPath(compare, W, H, 22, range) : "";

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ height }} aria-hidden>
      <defs>
        <linearGradient id={`ar-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity=".26" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <g stroke="var(--crm-hair)" strokeWidth="1">
        {[42, 88, 134, 180].map((y) => (
          <line key={y} x1="0" y1={y} x2={W} y2={y} />
        ))}
      </g>
      {line ? <path d={`${line} L${W},${H} L0,${H} Z`} fill={`url(#ar-${id})`} /> : null}
      {line ? (
        <path
          d={line}
          fill="none"
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
      {cmp ? (
        <path
          d={cmp}
          fill="none"
          stroke="var(--color-muted-foreground)"
          strokeWidth="2"
          strokeDasharray="3 8"
          strokeLinecap="round"
          opacity=".5"
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
    </svg>
  );
}
